










// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';

// Third party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import ButtonSwitch, {
  SwitchActiveColor,
} from '@/shared/components/button-switch.component.vue';
import DropUp, {
  DropUpColorHover,
} from '@/shared/components/DropUp.component.vue';
import EmptyState from '@/shared/components/empty-state.component.vue';
import GroupFilter from '@/shared/components/GroupFilter.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import ItemThumb, {
  TooltipColor,
} from '@/shared/components/item-thumb.component.vue';
import Tooltip from '@/shared/components/Tooltip.component.vue';
import Search, {
  SearchPredefinedStyle,
} from '@/shared/components/search.component.vue';import SearchAutocomplete from '@/shared/components/SearchAutocomplete.component.vue';
import TopBar from '@/shared/components/TopBar.component.vue';
import TopModal from '@/shared/components/TopModal.component.vue';
import TableColumnOrder from '@/shared/components/TableColumnOrder.component.vue';
import SpotlightText, {
  SpotlightTextColor,
} from '@/shared/components/SpotlightText.component.vue';
import CheckIcon, {
  CheckIconColor,
} from '@/shared/components/CheckIcon.component.vue';
import Datepicker, { DatepickerBorder } from '@/shared/components/datepicker/index.vue';

// Views
import AddPurchaseModal from '@/manager/purchase/views/convocation-detail/add-purchase-modal/index.vue';
import AddNoteModal from '@/manager/convocation/views/convocationDetail/addNoteModal/addNoteModal.view.vue';
import PurchaseList from '@/manager/purchase/views/convocation-detail/purchase-list/index.vue';

// Services
import UtilsService from '@/core/services/utils.service';
import {
  ConvocationService,
  TypeOfProduct,
} from '@/manager/convocation/convocation.service';

// Constants
import Assets from '@/constants/assets.ts';
import Constants from '@/constants/constants';
import RouteNames from '@/constants/route-names';

// Filters
import formatDate from '@/shared/filters/formatDate.filter.ts';

// Mixins
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import NotificationMessage from '@/shared/mixins/notificationMessage.mixin.ts';
import SearchMixin from '@/shared/mixins/search.mixin.ts';

// Models-Interfaces
import { Client } from '@/manager/client/client.model';
import { Convocation } from '@/manager/convocation/convocation.model';
import { TimetablesInterface } from '@/shared/interfaces/timetables.interface';
import { SecondLevelOptionModel } from '@/shared/models/secondLevelOption.model';
import { CounterInterface } from '@/shared/components/counter/counter.interface.ts';
import { Participant } from '@/manager/convocation/participant.model';
import { CounterColor } from '@/shared/components/counter/counter.enum.ts';

@Component({
  components: {
    AddNoteModal,
    AddPurchaseModal,
    Btn,
    ButtonSwitch,
    CheckIcon,
    Datepicker,
    DropUp,
    EmptyState,
    GroupFilter,
    Icon,
    ItemThumb,
    Pagination,
    PurchaseList,
    Search,
    SearchAutocomplete,
    SpotlightText,
    TableColumnOrder,
    Tooltip,
    TopBar,
    TopModal,
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
      getConvocation: 'getConvocation',
      isOpenNavbar: 'isOpenNavbar',
    }),
  },
  filters: {
    formatDate,
  },
})
export default class PurchaseConvocationDetail extends Mixins<
  NotificationMessage
>(NotificationMessage) {
  @Prop() private id!: string;

  private assets = Assets;
  private searchString = new URLSearchParams(window.location.href.split('?')[1]) && new URLSearchParams(window.location.href.split('?')[1]).get('search-string') ? new URLSearchParams(window.location.href.split('?')[1]).get('search-string') : '';
  private searchFiscalData:string = '';
  private searchNifString = '';
  private searchPredefinedStyle = SearchPredefinedStyle;
  private datepickerBorder = DatepickerBorder;
  private dropUpColorHover = DropUpColorHover;
  private tooltipColor = TooltipColor;
  private convocation = new Convocation();
  private iconEnum = IconEnum;
  private btnColor = BtnColor;
  private btnWidth = BtnWidth;
  private getConvocation!: any;
  private profile!: any;
  private disableTooltipAvatar = true;
  private isOpenNavbar!: any;
  private addPurchaseModalOpened = false;
  private getProfile!: any;
  private searchClient = '';
  private selectedClient = null;
  private utilsService = UtilsService;
  private interval!: any;
  private spotlightTextColor = SpotlightTextColor;
  private cleanSearch = false;
  private purchaseListLoaded = false;
  private purchaseList = { count: 0 };
  private convocationLoaded = false;
  private newPurchaseAdded: boolean = false;
  private routeNames = RouteNames;
  private headerClass = '';
  private columnsVisibility = {
    observations: true,
  };
  private alertDisplace = false;
  private checkIconColor = CheckIconColor;
  private dateFrom = null;
  private dateTo = null;

  @Watch('isOpenNavbar')
  private watchIsOpenNavbar(newVal: boolean, oldVal: boolean) {
    this.calculateHeaderClass();
    this.checkObservationsVisibility();
  }

  @Watch('searchString')
  private watchSearchString(newVal: string, oldVal: string) {
    if (newVal === '') {
      this.purchaseListLoaded = false;
    }
  }

  @Watch('dateTo')
  private watchDateTo(newVal: string, oldVal: string) {
    if (newVal !== oldVal && oldVal !== '') {
      this.purchaseListLoaded = false
    }
  }

  @Watch('dateFrom')
  private watchDateFrom(newVal: string, oldVal: string) {
    if (newVal !== oldVal && oldVal !== '') {
      this.purchaseListLoaded = false
    }
  }

  @Watch('searchFiscalData')
  private watchFiscalData(newVal: string, oldVal: string) {
    if (newVal !== oldVal) {
      this.purchaseListLoaded = false
    }
  }

  @Watch('searchNifString')
  private watchSearchNifString(newVal: string, oldVal: string) {
    if (newVal === '') {
      this.purchaseListLoaded = false
    }
  }

  @Watch('id')
  private watchConvocationId(newVal: string, oldVal: string) {
    this.convocationLoaded = false;
    this.initializeComponent();
  }

  private created() {
    this.initializeComponent();
  }

  get defaultDate() {
    const today = dayjs().toISOString();
    const from = dayjs().set('date', 1).set('month', 0).toISOString();
    return {
      from,
      to: today,
    };
  }

  get hideWaitingList() {
    return this.profile.email === 'recordartefotografia@gmail.com';
  }

  get openModal() {
    return this.addPurchaseModalOpened;
  }

  get listSwitchOptions(): any[] {
    return [
      { id: 1, text: 'Asistentes', activeColor: SwitchActiveColor.Primary },
      { id: 2, text: 'Equipo' },
    ];
  }

  get prevConvocation() {
    let prev = 'default-id';
    if (this.convocation.prevConvocation) {
      prev = this.convocation.prevConvocation;
    }
    return prev;
  }

  get nextConvocation() {
    let next = 'default-id';
    if (this.convocation.nextConvocation) {
      next = this.convocation.nextConvocation;
    }
    return next;
  }

  get tooltipAvatarText() {
    let result = 'Sin trainer asignado';
    this.disableTooltipAvatar = true;
    if (
      this.convocation !== undefined &&
      this.convocation.trainer !== null &&
      this.convocation.trainer !== undefined &&
      this.convocation.trainer.firstName !== null
    ) {
      this.disableTooltipAvatar = false;
      const trainer = this.convocation.trainer;
      result = `${trainer.firstName} ${trainer.lastName}`;
    }
    return result;
  }

  get itemTooltipColor() {
    let color = TooltipColor.Primary;
    if (this.isConference) {
      color = TooltipColor.Secondary;
    }
    return color;
  }

  get convocationDate() {
    return (this.convocation as any).timetables[0];
  }

  get convocationStartHour() {
    const timeStart = this.convocationDate.timeStart;
    const splittedTime = timeStart.split(':');

    return `${splittedTime[0]}:${splittedTime[1]}h`;
  }

  get avatar() {
    let result = this.assets.defaultAvatar;
    if (
      this.convocation !== undefined &&
      this.convocation.trainer !== null &&
      this.convocation.trainer !== undefined &&
      this.convocation.trainer.profile !== undefined &&
      this.convocation.trainer.profile.avatar !== null
    ) {
      result = this.convocation.trainer.profile.avatar;
    }
    return result;
  }

  get title() {
    let result: string | undefined = '...';
    if (this.convocation.product !== undefined) {
      result = this.convocation.product.name;
    }
    return result;
  }

  get location() {
    let result: string | undefined = 'Sin ubicación';
    if (this.convocation.location) {
      result = this.convocation.location.name;
    }
    return result;
  }

  get isConference() {
    return ConvocationService.convocationIsProductType(
      this.convocation,
      TypeOfProduct.Conference,
    );
  }

  get isCourse() {
    return ConvocationService.convocationIsProductType(
      this.convocation,
      TypeOfProduct.Course,
    );
  }

  get isFirstDay(): boolean {
    let result = false;
    const now = dayjs();
    const timetables = this.convocation.timetables;
    if (timetables) {
      const firstDay = dayjs(timetables[0].date, { locale: 'es' });
      if (now.diff(firstDay, 'days' as UnitType) === 0) {
        result = true;
      }
    }
    return result;
  }

  get existsUrlMap(): boolean {
    return this.convocation.urlMap !== null;
  }

  private dateRangeSelected(from: any, to: any) {
    if (from && to) {
      const dateFrom = UtilsService.setTimeToCero(dayjs(from)).toISOString();
      const dateTo = UtilsService.setTimeToLast(dayjs(to)).toISOString();

      this.dateFrom = dateFrom;
      this.dateTo = dateTo;
    } else {
      this.dateFrom = null;
      this.dateTo = null;
    }
  }

  private formatConvocationDates(convocation: Convocation): string {
    return ConvocationService.formatDates(
      convocation.timetables as TimetablesInterface[],
    );
  }

  private purchaseListLoadedEvent(list: any) {
    this.purchaseListLoaded = true;
    this.purchaseList = list;
  }

  private initializeComponent() {
    setTimeout(() => {
      this.fetchConvocation(() => {
        this.convocationLoaded = true;
      });
    }, 0);
  }

  private calculateHeaderClass() {
    const windowWidth = window.innerWidth;
    let headerClass = '';
    if (windowWidth < 1250 || (windowWidth < 1440 && this.isOpenNavbar)) {
      headerClass = 'header--too-narrow';
    }
    window.setTimeout(() => {
      this.headerClass = headerClass;
    }, 0);
  }

  private checkObservationsVisibility() {
    const windowWidth = window.innerWidth;
    let visibility = true;
    if (windowWidth < 1408 || (windowWidth < 1600 && this.isOpenNavbar)) {
      visibility = false;
    }
    window.setTimeout(() => {
      this.columnsVisibility.observations = visibility;
    }, 0);
  }

  private handleResize() {
    this.calculateHeaderClass();
    this.checkObservationsVisibility();
  }

  private mounted() {
    window.addEventListener('resize', this.handleResize);
  }

  private beforeDestroy() {
    clearInterval(this.interval);
    window.removeEventListener('resize', this.handleResize);
  }

  private elementVisible(element: any) {
    return element.offsetWidth > 0 && element.offsetHeight > 0;
  }

  private newPurchase() {
    this.showNotification('Compra añadida con éxito');
    this.newPurchaseAdded = true;
    this.refetchLists();
  }

  private refetchLists() {
    const purchaseListComponent = this.$refs.purchaseListComponent;
    (purchaseListComponent as PurchaseList).fetchFirstPageList();
  }

  private onError(
    message: string = 'Estamos sufriendo algún problema... inténtalo de nuevo',
  ) {
    this.showNotification(message, this.spotlightTextColor.Secondary);
  }

  private setAddPurchaseModalOpened(value: boolean) {
    this.addPurchaseModalOpened = value;
  }

  private openPurchaseModal() {
    this.setAddPurchaseModalOpened(true);
  }

  private fetchConvocation(
    callback: any = () => {
      const doNothing = 1;
    },
  ) {
    this.$store
      .dispatch('fetchConvocation', {
        id: this.id,
      })
      .then((res: any) => {
        this.convocation = Object.assign({}, res);
        callback();
      });
  }

  private showMessage(message: string) {
    this.showNotification(message, this.spotlightTextColor.Primary);
  }

  private topBarScrolled(scrolled: boolean) {
    this.alertDisplace = scrolled;
  }
}
